import { refreshAccessToken } from '@tshared/apis/auth.api'
import { jwtDecode } from 'jwt-decode'
import { isHTTPError, isNetworkError, isTimeoutError } from 'ky'

import { config } from '../config/config'
import type { User } from '../models'
import { expireUserSession, setUser, useUserStore } from '../stores/user-session.store'

const TOKEN_REFRESH_SKEW_SECONDS = 15 * 60

function shouldRefreshToken(accessToken?: string): boolean {
  if (!accessToken) return true

  try {
    const payload = jwtDecode<{ exp?: number }>(accessToken)
    return !payload.exp || payload.exp < Math.floor(Date.now() / 1_000) + TOKEN_REFRESH_SKEW_SECONDS
  } catch {
    return true
  }
}

function isUnrecoverableRefreshError(error: unknown): boolean {
  if (isHTTPError(error)) {
    return error.response.status === 401
  }

  if (isNetworkError(error) || isTimeoutError(error)) {
    return false
  }

  // A successful response without an access token is also an invalid refresh.
  return true
}

export async function refreshUser(force?: boolean): Promise<User | null> {
  const user = useUserStore.getState().user
  if (!user?.refreshToken) {
    if (force) {
      const error = new Error('refreshToken is not set')
      await expireUserSession()
      useUserStore.getState().onRefreshTokenError?.(error)
      throw error
    }
    return null
  }

  if (!force && !shouldRefreshToken(user.accessToken)) return user

  const refreshToken = user.refreshToken

  try {
    const nextUser = await refreshAccessToken(refreshToken)
    const mergedUser = {
      ...user,
      userName: nextUser.userName ?? user.userName,
      accessToken: nextUser.accessToken,
      refreshToken: nextUser.refreshToken,
      expiresTime: nextUser.expiresTime,
    }
    await setUser(mergedUser)
    return mergedUser
  } catch (error) {
    if (isUnrecoverableRefreshError(error)) {
      const adopted = await adoptUserRefreshedByAnotherTab(refreshToken)
      if (adopted) return adopted
      await expireUserSession()
    }
    useUserStore.getState().onRefreshTokenError?.(error)
    throw error
  }
}

async function adoptUserRefreshedByAnotherTab(failedRefreshToken: string): Promise<User | null> {
  if (!useUserStore.getState().persist) return null

  try {
    const raw = await config.getLoadKv()('user')
    const stored = raw ? (JSON.parse(raw) as User | null) : null
    if (!stored?.refreshToken || stored.refreshToken === failedRefreshToken) return null
    await setUser(stored)
    return stored
  } catch {
    return null
  }
}
